import {Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, signal,} from '@angular/core';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {TranslateModule} from '@ngx-translate/core';
import {MatActionList, MatListItemAvatar} from '@angular/material/list';
import {
  MatCard,
  MatCardContent,
  MatCardHeader,
  MatCardImage,
  MatCardSubtitle,
  MatCardTitle
} from '@angular/material/card';
import {MatRipple} from '@angular/material/core';
import {fromEvent, Subscription} from 'rxjs';
import {MatIcon} from "@angular/material/icon";
import {RecentRegistration} from "../../../core/definitions/recent.registration";
import {MAX_NUMBER_OF_RECENT_REGISTRATIONS} from "../../welcome-const";
import {Params, RouterLink} from "@angular/router";
import {FeatureFlagsService} from "../../../core/feature-flags.service";

const MIN_CARD_WIDTH = 80;

@Component({
    selector: 'app-recent-registrations-list',
  imports: [
    MatProgressSpinner,
    TranslateModule,
    MatListItemAvatar,
    MatActionList,
    MatCard,
    MatCardImage,
    MatCardHeader,
    MatCardTitle,
    MatCardSubtitle,
    MatCardContent,
    MatRipple,
    MatIcon,
    RouterLink
  ],
    templateUrl: './recent-registrations-list.component.html',
    styleUrl: './recent-registrations-list.component.scss'
})
export class RecentRegistrationsListComponent implements OnChanges, OnDestroy {
  // Injections
  private readonly el: ElementRef = inject(ElementRef);
  private readonly featureFlags: FeatureFlagsService = inject(FeatureFlagsService);

  @Input() loading = false;
  @Input() registrations: RecentRegistration[] = [];

  @Output() registrationClicked = new EventEmitter<RecentRegistration>();

  visibleCards = signal<RecentRegistration[]>([]);
  placeholderItems = signal<any[]>([]);

  subscriptions: Subscription[] = [];
  failedImages = new Set<string>();

  constructor() {
    this.subscriptions.push(fromEvent(window, 'resize').subscribe(() => {
      this.calculateVisibleItems();
    }));
  }

  ngOnChanges() {
    this.calculateVisibleItems();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  getRouterLink(registration: RecentRegistration): any[] {
    if (this.featureFlags.getFeatureFlags()?.experimental?.useNewObjectPage) {
      return ['/artifact', registration.artifact_id];
    }

    return ['/artifact'];
  }

  getQueryParams(registration: RecentRegistration): Params {
    return {
      artifactId: registration.artifact_id
    }
  }

  onRegistrationClicked(_: unknown, registration: RecentRegistration) {
    this.registrationClicked.emit(registration);
  }

  onImageLoad(registration: RecentRegistration, success: boolean) {
    if (!success) {
      this.failedImages.add(registration.artifact_id);
    } else {
      this.failedImages.delete(registration.artifact_id);
    }
  }

  private calculateVisibleItems() {
    const el = this.el.nativeElement;
    const containerWidth = el.clientWidth;
    const screenWidth = window.innerWidth;
    const halfGap = screenWidth <= 1024 ? 4 : 8;
    const numCards = Math.min(Math.floor(containerWidth / (MIN_CARD_WIDTH + halfGap)), MAX_NUMBER_OF_RECENT_REGISTRATIONS)
    if (screenWidth <= 600) {
      // up to two rows for small screens
      this.visibleCards.set(this.registrations.slice(0, numCards * 2));
      let numPlaceholders = numCards * 2 - this.registrations.length;
      this.placeholderItems.set(Array(Math.max(numPlaceholders, 0)).fill(null));
    } else {
      const numPlaceholders = numCards - (this.registrations.length);
      this.visibleCards.set(this.registrations.slice(0, numCards));
      this.placeholderItems.set(Array(Math.max(numPlaceholders, 0)).fill(null));
    }
  }

}
